import { useState, useMemo } from 'react';
import { X, Loader2, Sticker, Image as ImageIcon, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import StickerPanel from '../presentation-editor/StickerPanel';
import { API } from '../../config/api';
import { api } from '../../utils/api';
import { uploadMedia } from '../../utils/uploadMedia';
import { useTranslation } from '../../i18n';

// Teacher-side modal to push a word into a specific student's homework
// mid-lesson. Word/translation are required; transcription and image are
// optional. The image can be uploaded, chosen from the sticker library, or
// left blank. On submit we POST /api/homework/live-vocabulary — the server
// finds or creates the student's assignment for this scheduled lesson.
const LiveVocabularyModal = ({ lessonScheduleId, students, defaultStudentId, onClose, onAdded }) => {
  const { t } = useTranslation();

  const initialStudent = useMemo(() => {
    if (defaultStudentId && students.some((s) => s.id === defaultStudentId)) return defaultStudentId;
    return students[0]?.id || '';
  }, [defaultStudentId, students]);

  const [studentId, setStudentId] = useState(initialStudent);
  const [word, setWord] = useState('');
  const [translation, setTranslation] = useState('');
  const [transcription, setTranscription] = useState('');
  const [image, setImage] = useState(null); // { url, fileName } | null
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = studentId && word.trim() && translation.trim() && !submitting;

  const handlePickSticker = ({ src }) => {
    if (!src) return;
    const url = src.startsWith('http') ? src : `${API.BASE}${src}`;
    const fileName = src.split('/').pop() || 'sticker.png';
    setImage({ url, fileName });
  };

  const handleUploadImage = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('virtualClassroom.liveVocab.imageTooLarge'));
      return;
    }
    setUploading(true);
    try {
      const url = await uploadMedia(file, 'image');
      setImage({ url, fileName: file.name });
    } catch (err) {
      toast.error(t('virtualClassroom.liveVocab.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = {
        studentId,
        lessonScheduleId,
        word: {
          word: word.trim(),
          translation: translation.trim(),
          transcription: transcription.trim() || undefined,
          image: image || undefined,
        },
      };
      const { data, error } = await api.post('/api/homework/live-vocabulary', body);
      if (error) {
        toast.error(error || t('virtualClassroom.liveVocab.failed'));
        return;
      }
      const studentName = students.find((s) => s.id === studentId)?.name || '';
      toast.success(t('virtualClassroom.liveVocab.added', { name: studentName || t('virtualClassroom.liveVocab.student') }));
      onAdded?.(data);
      onClose?.();
    } catch (err) {
      console.error('addLiveVocabulary failed:', err);
      toast.error(t('virtualClassroom.liveVocab.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('virtualClassroom.liveVocab.title')}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition"
          >
            <X size={18} />
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          {t('virtualClassroom.liveVocab.subtitle')}
        </p>

        {students.length > 1 && (
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('virtualClassroom.liveVocab.studentLabel')}
            </label>
            <select
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A4D25] focus:border-transparent bg-white"
            >
              {students.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('virtualClassroom.liveVocab.wordLabel')}
            </label>
            <input
              type="text"
              value={word}
              onChange={(e) => setWord(e.target.value)}
              placeholder="Apple"
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A4D25] focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('virtualClassroom.liveVocab.translationLabel')}
            </label>
            <input
              type="text"
              value={translation}
              onChange={(e) => setTranslation(e.target.value)}
              placeholder={t('virtualClassroom.liveVocab.translationPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A4D25] focus:border-transparent"
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('virtualClassroom.liveVocab.transcriptionLabel')}
          </label>
          <input
            type="text"
            value={transcription}
            onChange={(e) => setTranscription(e.target.value)}
            placeholder="/ˈæp.əl/"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A4D25] focus:border-transparent"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('virtualClassroom.liveVocab.imageLabel')}
          </label>
          {image ? (
            <div className="flex items-center gap-3">
              <div className="relative">
                <img
                  src={image.url}
                  alt=""
                  className="h-16 w-16 object-cover rounded border border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => setImage(null)}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                >
                  <X size={12} />
                </button>
              </div>
              <button
                type="button"
                onClick={() => setShowStickerPicker(true)}
                className="text-xs text-[#2A4D25] hover:underline inline-flex items-center gap-1"
              >
                <Sticker size={14} />
                {t('virtualClassroom.liveVocab.changeSticker')}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <label className={`px-3 py-2 border border-dashed rounded-lg flex items-center gap-2 cursor-pointer transition ${uploading ? 'opacity-60 cursor-wait border-gray-300' : 'border-gray-300 hover:bg-gray-50'}`}>
                <input
                  type="file"
                  accept="image/*"
                  disabled={uploading}
                  onChange={(e) => {
                    handleUploadImage(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                  className="hidden"
                />
                <ImageIcon size={16} className="text-gray-400" />
                <span className="text-sm text-gray-500">
                  {uploading ? t('virtualClassroom.liveVocab.uploading') : t('virtualClassroom.liveVocab.uploadImage')}
                </span>
              </label>
              <button
                type="button"
                onClick={() => setShowStickerPicker(true)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#2A4D25] hover:bg-[#2A4D25]/5 inline-flex items-center gap-2"
              >
                <Sticker size={14} />
                {t('virtualClassroom.liveVocab.pickSticker')}
              </button>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            {t('virtualClassroom.liveVocab.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#2A4D25' }}
            onMouseEnter={(e) => { if (canSubmit) e.currentTarget.style.backgroundColor = '#1e3a1a'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#2A4D25'; }}
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            {t('virtualClassroom.liveVocab.addToHomework')}
          </button>
        </div>

        {showStickerPicker && (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4"
            onClick={() => setShowStickerPicker(false)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <StickerPanel
                onAddSticker={handlePickSticker}
                onClose={() => setShowStickerPicker(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveVocabularyModal;
